import { Component, useEffect } from 'react'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { Routes } from 'react-router-dom'
import { Provider, useSelector } from 'react-redux'
import { ApolloProvider } from '@apollo/client'
import { hotjar } from 'react-hotjar'

import SentryErrorBoundary from '~/core/SentryErrorBoundary'
import ApiBuilder from '~/apps/ApiBuilder'
import logger from '~/lib/logger'
import isFlagEnabled from '~/lib/isFlagEnabled'
import { getHotjarIdentityObj } from '~/lib/analytics/actions/hotjarIdentity'
import setUtmCookie from '~/lib/setUtmCookie'
import SuspenseLoader from '~/core/SuspenseLoader'
import { NavigationConfigProvider } from '~/core/NavigationConfig'
import Notification from '~/core/Notification'
import ResponsiveLayout from '~/components/ResponsiveLayout'
import ModalSessionTimedOut from '~/apps/consumer/components/ModalSessionTimedOut'
import {
    clearReturnTo,
    getPreferredAppRedirectPath,
} from '~/redux-modules/preferences'
import { identifyUser, getUserIdentity } from '~/redux-modules/identity'
import { getenv } from '~/settings'
import { actions as graphqlActions } from '~/redux-modules/graphql'
import {
    publicRoutes,
    privateRoutes,
    conditionalRoutes,
    redirects,
    conditionalRedirects,
} from '~/apps/routes/configs'
import {
    mapRoutesToReactRouter,
    mapRedirectsToReactRouter,
} from '~/apps/routes/utils'
import { mapRouteConfigToRouteComponent } from '~/apps/routes/utils/mapRouteConfigToRouteComponent'
import { useRouterTrack } from '~/lib/analytics/hooks/useRouterTrack'
import { VwoProvider } from '~/lib/providers'

const ONE_SECOND = 1000

const AppRoutes = () => {
    const identity = useSelector(state => getUserIdentity(state))
    const preferredRedirectPath = useSelector(state =>
        getPreferredAppRedirectPath(state),
    )

    useRouterTrack()

    const showQCWorkbench = isFlagEnabled(
        identity,
        'QC_WORKBENCH_MAINTENANCE',
        'on',
    )

    const isAutoOTEnabled = isFlagEnabled(identity, 'ENABLE_AUTO_OT_UI', 'true')

    const isAdmin = identity.isAdmin

    useEffect(() => {
        if (isAdmin === false && !window.hj) {
            //This function may be called multiple times so !window.hj to prevent
            //hotjar being initialized multiple times
            const hotjarConfigString = getenv('HOTJAR_ID')

            try {
                const { hjid, hjsv } = JSON.parse(hotjarConfigString || '{}')

                if (hjid && hjsv && identity) {
                    hotjar.initialize(hjid, hjsv)

                    const { id } = identity

                    window.hj('identify', id, getHotjarIdentityObj(identity))
                }
            } catch (err) {
                logger.exception(err, {
                    extra: {
                        info: 'Failed to init hotjar',
                    },
                })
            }
        }
    }, [identity])

    if (identity.loading) {
        return <SuspenseLoader />
    }

    const rootRedirect = preferredRedirectPath

    const routerRoutes = mapRoutesToReactRouter([
        ...publicRoutes,
        ...privateRoutes,
        ...conditionalRoutes({
            isAutoOTEnabled,
        }),
    ])
    const routerRedirects = mapRedirectsToReactRouter([
        ...redirects,
        ...conditionalRedirects({
            isAdmin,
            showQCWorkbench,
            rootRedirect,
            isAutoOTEnabled,
        }),
    ])

    const allRoutes = [...routerRedirects, ...routerRoutes]

    return (
        <>
            <NavigationConfigProvider>
                <ResponsiveLayout>
                    <Routes>
                        {allRoutes.map(mapRouteConfigToRouteComponent)}
                    </Routes>
                </ResponsiveLayout>
            </NavigationConfigProvider>
            <ModalSessionTimedOut />
            <Notification />
        </>
    )
}

class App extends Component {
    constructor() {
        super()

        const { client, store } = ApiBuilder.create({
            appName: 'consumer',
        })

        this.client = client
        this.store = store
    }

    componentDidMount() {
        const { dispatch } = this.store

        // Trigger the one identity call for this entire
        // application session.
        dispatch(identifyUser())

        setUtmCookie()

        // Not use returnTo anymore, need to clear returnTo on localStorage
        this.timer = setTimeout(() => dispatch(clearReturnTo()), ONE_SECOND)
        dispatch(graphqlActions.setApolloClient(this.client))
    }

    componentWillUnmount() {
        if (this.timer) {
            clearTimeout(this.timer)
        }
    }

    render() {
        return (
            <HelmetProvider>
                <ApolloProvider client={this.client}>
                    <Provider store={this.store}>
                        <SentryErrorBoundary>
                            <Helmet>
                                <title>Fictiv</title>
                            </Helmet>
                            <VwoProvider>
                                <AppRoutes />
                            </VwoProvider>
                        </SentryErrorBoundary>
                    </Provider>
                </ApolloProvider>
            </HelmetProvider>
        )
    }
}

export default App
